<template>
  <section class="tasks-panel">
    <div class="panel-header">
      <div>
        <p class="panel-kicker">Tasks</p>
        <h2>Focus list</h2>
      </div>
    </div>

    <form
      v-if="editingIndex !== null"
      class="panel-form"
      @submit.prevent="handleSubmit"
    >
      <input v-model="form.title" type="text" placeholder="Title" required />
      <textarea
        v-model="form.description"
        rows="2"
        placeholder="Details (optional)"
      ></textarea>
      <div class="form-row">
        <select v-model="form.priority">
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <input v-model="form.category" type="text" placeholder="Category" />
        <input v-model="form.dueDate" type="date" />
      </div>
      <div class="form-actions">
        <button type="button" class="btn-ghost" @click="resetForm">
          Cancel
        </button>
        <button type="submit" class="btn-primary">
          {{ editingIndex === null ? "Save" : "Update" }}
        </button>
      </div>
    </form>

    <div v-if="dashboardStore.isLoadingTasks" class="panel-empty">
      Loading tasks...
    </div>
    <div v-else-if="dashboardStore.tasks.length === 0" class="panel-empty">
      No tasks yet.
    </div>

    <ul class="task-list">
      <li
        v-for="(task, index) in dashboardStore.tasks"
        :key="task.id || `${task.title}-${index}`"
        :class="['task-item', { completed: task.completed }]"
        @click="startEdit(index)"
      >
        <label>
          <input
            type="checkbox"
            :checked="task.completed"
            @click.stop
            @change="toggleComplete(index)"
          />
          <span class="task-title">{{ task.title }}</span>
        </label>
        <div class="task-meta">
          <span v-if="task.category">{{ task.category }}</span>
          <span class="priority" :data-priority="task.priority">
            {{ task.priority }}
          </span>
          <span v-if="task.dueDate">{{ formatDate(task.dueDate) }}</span>
        </div>
        <p v-if="task.description" class="task-desc">{{ task.description }}</p>
        <button type="button" class="icon-btn" @click.stop="removeTask(index)">
          ×
        </button>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from "vue";
import { useDashboardStore } from "@/stores/dashboard";

const dashboardStore = useDashboardStore();
const editingIndex = ref<number | null>(null);

const form = reactive({
  title: "",
  description: "",
  priority: "medium" as "low" | "medium" | "high",
  category: "",
  dueDate: "",
});

onMounted(() => {
  dashboardStore.fetchTasks();
});

const resetForm = (): void => {
  form.title = "";
  form.description = "";
  form.priority = "medium";
  form.category = "";
  form.dueDate = "";
  editingIndex.value = null;
};

const handleSubmit = (): void => {
  if (!form.title.trim()) return;
  const payload = {
    title: form.title.trim(),
    description: form.description.trim() || null,
    completed: false,
    priority: form.priority,
    category: form.category.trim() || null,
    dueDate: form.dueDate || null,
  };
  if (editingIndex.value === null) return;
  const existing = dashboardStore.tasks[editingIndex.value];
  dashboardStore.updateTask(editingIndex.value, {
    ...existing,
    ...payload,
  });
  resetForm();
};

const toggleComplete = (index: number): void => {
  dashboardStore.toggleTaskCompleted(index);
};

const removeTask = (index: number): void => {
  dashboardStore.removeTask(index);
};

const startEdit = (index: number): void => {
  const task = dashboardStore.tasks[index];
  if (!task) return;
  form.title = task.title;
  form.description = task.description || "";
  form.priority = task.priority || "medium";
  form.category = task.category || "";
  form.dueDate = task.dueDate || "";
  editingIndex.value = index;
};

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};
</script>

<style scoped>
.tasks-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-kicker {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a7a7a;
}

.panel-header h2 {
  margin: 4px 0 0 0;
  font-size: 18px;
  font-weight: 600;
  color: #222;
}

.panel-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid #ececec;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
}

.panel-form input,
.panel-form textarea,
.panel-form select {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e3e3e3;
  font-size: 12px;
  background: #fafafa;
  color: #222;
  transition:
    border 0.2s ease,
    box-shadow 0.2s ease;
}

.panel-form input:focus,
.panel-form textarea:focus,
.panel-form select:focus {
  outline: none;
  border-color: #1f73e6;
  box-shadow: 0 0 0 3px rgba(31, 115, 230, 0.15);
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-primary,
.btn-ghost {
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #1f73e6;
  color: white;
}

.btn-primary:hover {
  background: #1557b0;
}

.btn-ghost {
  background: transparent;
  color: #333;
  border-color: #e0e0e0;
}

.btn-ghost:hover {
  background: #f6f6f6;
}

.panel-empty {
  color: #777;
  font-size: 12px;
}

.task-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-item {
  position: relative;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: white;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.task-item.completed {
  opacity: 0.7;
  text-decoration: line-through;
}

.task-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.task-title {
  font-weight: 600;
}

.task-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #666;
}

.task-desc {
  margin: 0;
  font-size: 12px;
  color: #444;
}

.priority[data-priority="high"] {
  color: #d33b27;
}

.priority[data-priority="medium"] {
  color: #f29900;
}

.priority[data-priority="low"] {
  color: #2e7d32;
}

.icon-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  color: #666;
}

.icon-btn:hover {
  color: #111;
}
</style>
